/**
 * Simple Bundles – Infinite Options Image Swap v2 (17 Feb 2026)
 *
 * Swaps the main PDP image when a customer changes an Infinite Options
 * bundle dropdown. Uses the bundled_variants metafield for reliable
 * product/variant → image mapping. Should work acrosss any Shopify theme.
 *
 * ─────────────────────────────────────────────────────────────────────
 * INSTALLATION (pick one):
 *
 *   Option A – Theme asset (recommended):
 *     1. Upload this file to your theme's assets/ folder
 *     2. Add before </body> in layout/theme.liquid:
 *        <script src="{{ 'sb-image-swap.js' | asset_url }}" defer></script>
 *
 *   Option B – Inline snippet:
 *     Wrap in <script>…</script> at the bottom of your product template
 *
 * ─────────────────────────────────────────────────────────────────────
 * HOW IT WORKS:
 *
 *   1. On page load, fetches the current product's metafields via
 *      /products/{handle}.json to get the bundled_variants data
 *   2. Builds a lookup map: option display text → { handle, variant_id }
 *   3. Fetches each child product once (cached) to get all variant images
 *   4. On dropdown change, looks up the selection → swaps the main image
 *
 *   Because it uses the actual metafield data (handles + variant IDs),
 *   it never has to guess product handles from display text.
 * ─────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ========================================================================
  // CONFIG
  // ========================================================================
  const CONFIG = {
    // Selectors to find the main product image – tried in order. Most themes use these selectors .
    // Add your theme's selector at the top if none of these match.
    imageSelectors: [
      '.product__media-item--variant img',
      '.product__media img.image-magnify-full-size',
      '.product__media-list .product__media img',
      '.product__media img',
      '.product-image-main img',
      '.product__photos img',
      '.product__photo img.photoswipe__image',
      '.product__photo img',
      '.product-gallery__image img',
      '.product__main-photos img',
      '.product-main-slide .image-wrap img',
      '.product-featured-img',
      '.product-single__photo img',
      '.product-single__media img',
      '.product__media-item img',
      '[data-product-media-type="image"] img',
      '.product-single__image img',
      '.product__image-wrapper img',
      '.product-featured-image img'
    ],

    ioBundleContainer: '#simple-bundles-io-options',
    transitionMs: 200,
  };

  // ========================================================================
  // STATE
  // ========================================================================
  let mainImageEl = null;
  let originalSrc = null;
  let originalSrcset = null;

  // Map: normalized option text → image URL
  // e.g. "cotton canvas cap" → "https://cdn.shopify.com/..."
  const imageMap = {};

  // Cache for /products/{handle}.js fetches
  const productCache = {};

  // ========================================================================
  // HELPERS
  // ========================================================================

  /**
   * Normalize option text for reliable matching.
   * Strips price suffixes like "(+CA$5.00)", whitespace, and lowercases.
   */
  function normalizeOptionText(text) {
    return text
      .replace(/\([\+\-]?\s*[A-Z]{0,3}\$?\s*[\d,.]+\)/g, '') // (+CA$5.00), (-$3.00), etc.
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * Find the main product image on the page.
   * Tries configured selectors, then falls back to largest image in product area.
   */
  function findMainImage() {
    for (const sel of CONFIG.imageSelectors) {
      const img = document.querySelector(sel);
      if (img && img.src) return img;
    }

    // Fallback: largest image in the product section
    const section =
      document.querySelector('.product') ||
      document.querySelector('[data-section-type="product"]') ||
      document.querySelector('.product-single') ||
      document.querySelector('main');

    if (!section) return null;

    let best = null;
    let bestArea = 0;
    section.querySelectorAll('img').forEach((img) => {
      const area = img.offsetWidth * img.offsetHeight;
      if (area > bestArea && !img.closest(CONFIG.ioBundleContainer)) {
        best = img;
        bestArea = area;
      }
    });
    return best;
  }

  /**
   * Get the current product handle from the URL.
   */
  function getProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /**
   * Fetch product JSON (cached).
   */
  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      productCache[handle] = data;
      return data;
    } catch (e) {
      console.warn('[SB-IMG] fetch failed for', handle, e);
      return null;
    }
  }

  /**
   * Resize a Shopify CDN image URL to a given width.
   */
  function resizeImage(url, width) {
    if (!url) return url;
    // Remove any existing size suffix and add desired one
    return url
      .replace(/_(\d+x\d*|\d*x\d+)\./, '.')
      .replace(/\.(\w+)(\?.*)?$/, `_${width}x.$1$2`);
  }

  /**
   * Swap the main image with a fade transition.
   */
  function swapImage(newSrc) {
    if (!mainImageEl || !newSrc) return;

    // Avoid swapping to the same image
    const normCurrent = mainImageEl.src.replace(/[?#].*$/, '').replace(/_\d+x\d*\./, '.');
    const normNew = newSrc.replace(/[?#].*$/, '').replace(/_\d+x\d*\./, '.');
    if (normCurrent === normNew) return;

    mainImageEl.style.transition = `opacity ${CONFIG.transitionMs}ms ease`;
    mainImageEl.style.opacity = '0';

    setTimeout(() => {
      // Remove srcset so our src takes priority
      mainImageEl.removeAttribute('srcset');

      // Update parent <source> elements if inside a <picture>
      const picture = mainImageEl.closest('picture');
      if (picture) {
        picture.querySelectorAll('source').forEach((s) => s.remove());
      }

      // Update zoom / lightbox wrappers
      const zoomEl =
        mainImageEl.closest('[data-zoom]') ||
        mainImageEl.closest('[data-bgset]') ||
        mainImageEl.closest('[data-image]');
      if (zoomEl) {
        zoomEl.dataset.zoom && (zoomEl.dataset.zoom = resizeImage(newSrc, 2048));
        zoomEl.dataset.bgset && (zoomEl.dataset.bgset = newSrc);
      }

      mainImageEl.src = newSrc;

      const show = () => { mainImageEl.style.opacity = '1'; };
      mainImageEl.addEventListener('load', show, { once: true });
      // If cached / already loaded
      if (mainImageEl.complete) show();
    }, CONFIG.transitionMs);
  }

  function restoreOriginal() {
    if (!mainImageEl || !originalSrc) return;
    mainImageEl.style.transition = `opacity ${CONFIG.transitionMs}ms ease`;
    mainImageEl.style.opacity = '0';
    setTimeout(() => {
      mainImageEl.src = originalSrc;
      if (originalSrcset) mainImageEl.setAttribute('srcset', originalSrcset);
      mainImageEl.style.opacity = '1';
    }, CONFIG.transitionMs);
  }

  // ========================================================================
  // CORE: Build the image lookup map from bundled_variants metafield
  // ========================================================================

  /**
   * Parse the bundled_variants metafield and fetch images for each
   * child product/variant. Populates the global `imageMap`.
   *
   * bundled_variants structure (from SB metafields):
   * [
   *   {
   *     "variant_title": "Default Title",
   *     "variant_id": 123456,
   *     "handle": "sample_product_handle",
   *     "product_title": "Sample Product",
   *     "variant_options": [{ "name": "Option 1", "value": "Sample Product" }]
   *   },
   *   ...
   * ]
   *
   * The dropdown display text matches variant_options[].value
   * (e.g. "Sample Product", "Custom Water Bottle - Cornflower / Dark Berry")
   */
  async function buildImageMap(bundledVariants) {
    if (!Array.isArray(bundledVariants) || bundledVariants.length === 0) {
      console.warn('[SB-IMG] No bundled_variants data found.');
      return;
    }

    // Group by product handle to minimize fetches
    const handleGroups = {};
    for (const bv of bundledVariants) {
      if (!bv.handle) continue;
      if (!handleGroups[bv.handle]) handleGroups[bv.handle] = [];
      handleGroups[bv.handle].push(bv);
    }

    // Fetch each unique child product
    const fetches = Object.keys(handleGroups).map(async (handle) => {
      const product = await fetchProduct(handle);
      if (!product) {
        console.warn('[SB-IMG] Could not fetch product:', handle);
        return;
      }

      for (const bv of handleGroups[handle]) {
        let imageSrc = null;

        // 1. Try to find variant-specific image by variant_id
        const matchedVariant = product.variants.find(
          (v) => v.id === bv.variant_id
        );
        if (matchedVariant && matchedVariant.featured_image) {
          imageSrc = matchedVariant.featured_image.src;
        }

        // 2. Fall back to product featured image
        if (!imageSrc && product.featured_image) {
          imageSrc = product.featured_image;
        }

        // 3. Fall back to first product image
        if (!imageSrc && product.images && product.images.length > 0) {
          imageSrc = product.images[0];
        }

        if (!imageSrc) {
          console.log('[SB-IMG] No image available for:', bv.product_title);
          continue;
        }

        // Request a good PDP size
        imageSrc = resizeImage(imageSrc, 1024);

        // Map all possible text keys for this variant
        const keys = new Set();

        // From variant_options (primary – matches dropdown display text)
        if (bv.variant_options) {
          for (const opt of bv.variant_options) {
            if (opt.value) keys.add(normalizeOptionText(opt.value));
          }
        }

        // From product_title + variant_title combo
        if (bv.product_title) {
          keys.add(normalizeOptionText(bv.product_title));
          if (bv.variant_title && bv.variant_title !== 'Default Title') {
            keys.add(
              normalizeOptionText(
                `${bv.product_title} - ${bv.variant_title}`
              )
            );
          }
        }

        for (const key of keys) {
          imageMap[key] = imageSrc;
        }
      }
    });

    await Promise.all(fetches);
    console.log('[SB-IMG] Image map built:', Object.keys(imageMap).length, 'entries', imageMap);
  }

  /**
   * Get bundled_variants data. Tries multiple sources:
   *   1. Fetch from /products/{handle}.json (most reliable, works on any theme)
   *   2. Parse from page JSON-LD / script tags (fallback)
   */
  async function getBundledVariants() {
    const handle = getProductHandle();
    if (!handle) return null;

    // Fetch the full product JSON which includes metafields on some themes
    // But Shopify's /products/{handle}.json doesn't include metafields,
    // so we use the Storefront-compatible metafield endpoint instead.
    try {
      const res = await fetch(
        `/products/${handle}.json`
      );
      if (res.ok) {
        const data = await res.json();
        const product = data.product || data;

        // Check if metafields are embedded (some themes do this)
        if (product.metafields) {
          const sbMeta =
            product.metafields.simple_bundles ||
            product.metafields['app--4901177--simple_bundles'];
          if (sbMeta && sbMeta.bundled_variants) {
            return JSON.parse(sbMeta.bundled_variants);
          }
        }
      }
    } catch (e) {
      // Continue to next method
    }

    // Method 2: Look for metafield data in <script type="application/json"> tags
    const scripts = document.querySelectorAll('script[type="application/json"]');
    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent);
        // Look for bundled_variants in various structures
        const searchObj = (obj, depth = 0) => {
          if (depth > 5 || !obj || typeof obj !== 'object') return null;
          if (obj.bundled_variants) {
            const val = obj.bundled_variants;
            return typeof val === 'string' ? JSON.parse(val) : val;
          }
          for (const key in obj) {
            const result = searchObj(obj[key], depth + 1);
            if (result) return result;
          }
          return null;
        };
        const found = searchObj(data);
        if (found) return found;
      } catch (e) {
        // Not the right JSON block
      }
    }

    // Method 3: Use Liquid-injected global (if the merchant added our snippet)
    if (window.__SB_BUNDLED_VARIANTS__) {
      return typeof window.__SB_BUNDLED_VARIANTS__ === 'string'
        ? JSON.parse(window.__SB_BUNDLED_VARIANTS__)
        : window.__SB_BUNDLED_VARIANTS__;
    }

    return null;
  }

  // ========================================================================
  // DROPDOWN EVENT HANDLING
  // ========================================================================

  function handleChange(select) {
    const selected = select.options[select.selectedIndex];
    if (!selected || !selected.value) {
      restoreOriginal();
      return;
    }

    const raw = selected.textContent;
    const normalized = normalizeOptionText(raw);

    console.log('[SB-IMG] Dropdown changed:', { raw: raw.trim(), normalized });

    const imageSrc = imageMap[normalized];
    if (imageSrc) {
      console.log('[SB-IMG] ✓ Image found:', imageSrc);
      swapImage(imageSrc);
    } else {
      // No image mapped – try partial matching as a fallback
      const partialMatch = Object.keys(imageMap).find(
        (key) => normalized.includes(key) || key.includes(normalized)
      );
      if (partialMatch) {
        console.log('[SB-IMG] ✓ Partial match:', partialMatch);
        swapImage(imageMap[partialMatch]);
      } else {
        console.log('[SB-IMG] ✗ No image for:', normalized);
        // Don't restore original – keep the last swapped image
      }
    }
  }

  function attachListeners(container) {
    const selects = container.querySelectorAll('select');
    selects.forEach((sel) => {
      if (sel.dataset.sbImgBound) return;
      sel.dataset.sbImgBound = '1';
      sel.addEventListener('change', () => handleChange(sel));
      console.log('[SB-IMG] Listener attached:', sel.name || sel.id || '(unnamed)');
    });
  }

  // ========================================================================
  // INIT
  // ========================================================================

  async function init() {
    // 1. Find main image
    mainImageEl = findMainImage();
    if (!mainImageEl) {
      console.warn('[SB-IMG] Main product image not found. Retrying in 1s...');
      setTimeout(() => {
        mainImageEl = findMainImage();
        if (mainImageEl) {
          console.log('[SB-IMG] Main image found on retry.');
          originalSrc = mainImageEl.src;
          originalSrcset = mainImageEl.getAttribute('srcset');
        } else {
          console.error('[SB-IMG] Could not find main product image.');
        }
      }, 1000);
    } else {
      originalSrc = mainImageEl.src;
      originalSrcset = mainImageEl.getAttribute('srcset');
      console.log('[SB-IMG] Main image found:', originalSrc);
    }

    // 2. Get bundled_variants and build image map
    const bundledVariants = await getBundledVariants();

    if (bundledVariants) {
      console.log('[SB-IMG] bundled_variants loaded:', bundledVariants.length, 'entries');
      await buildImageMap(bundledVariants);
    } else {
      console.warn(
        '[SB-IMG] Could not load bundled_variants from metafields.',
        'Add the Liquid snippet (see below) for guaranteed compatibility.',
        '\n\nAdd this to your product template:\n',
        '<script>',
        '  window.__SB_BUNDLED_VARIANTS__ = {{ product.metafields.simple_bundles.bundled_variants.value | json }};',
        '</script>'
      );

      // Even without metafield data, try the AJAX handle-guessing approach as fallback
      console.log('[SB-IMG] Falling back to AJAX handle lookup...');
    }

    // 3. Attach to existing dropdowns
    const ioContainer = document.querySelector(CONFIG.ioBundleContainer);
    if (ioContainer) attachListeners(ioContainer);

    // 4. Watch for dynamically injected dropdowns
    const observer = new MutationObserver((mutations) => {
      for (const mut of mutations) {
        for (const node of mut.addedNodes) {
          if (node.nodeType !== 1) continue;
          const container =
            node.matches && node.matches(CONFIG.ioBundleContainer)
              ? node
              : node.querySelector
                ? node.querySelector(CONFIG.ioBundleContainer)
                : null;
          if (container) attachListeners(container);
          if (node.tagName === 'SELECT') {
            const parent = node.closest(CONFIG.ioBundleContainer);
            if (parent) attachListeners(parent);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    console.log('[SB-IMG] Initialized.');
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();